// src/routes/UserRoutes.js
import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// Pages
import TermsOfService from "../components/User/TermsOfService";
import PrivacyPolicy from "../components/User/PrivacyPolicy";
import HomePage from "../components/User/HomePage";

/*
  🧭 ScrollToHashElement
  - Watches URL hash changes (e.g. /#contact)
  - Scrolls smoothly to target section after navigation
  - Works perfectly with react-router-hash-link (even across routes)
*/
/**
 * Robust scroll handler using scroll-margin-top
 * - Sets --header-height CSS var dynamically
 * - Adds .scroll-offset helper class to allow consistent offset with scrollIntoView
 */
export function ScrollToHashElement() {
    const { hash } = useLocation();

    useEffect(() => {
        if (!hash) return;
        const id = hash.replace("#", "");
        let retries = 0;
        const maxRetries = 25;
        const delay = 100;

        // ensure the CSS helper exists once
        if (!document.getElementById("scroll-offset-styles")) {
            const style = document.createElement("style");
            style.id = "scroll-offset-styles";
            style.innerHTML = `
        /* class to apply to sections we want to offset from the sticky header */
        .scroll-offset {
          scroll-margin-top: calc(var(--header-height, 80px) + 8px);
        }
      `;
            document.head.appendChild(style);
        }

        const tryScroll = () => {
            const el = document.getElementById(id);
            if (el) {
                // dynamic header height detection
                const header = document.querySelector("header") || document.getElementById("site-header");
                const headerHeight = header ? Math.round(header.getBoundingClientRect().height) : 0;

                // set CSS variable so .scroll-offset works for browser-native scrolling
                document.documentElement.style.setProperty("--header-height", `${headerHeight}px`);

                // ensure target has helper class (safe to add repeatedly)
                if (!el.classList.contains("scroll-offset")) el.classList.add("scroll-offset");

                // debug logs (remove when satisfied)
                // eslint-disable-next-line no-console
                console.log(`[ScrollToHashElement] target #${id} found. headerHeight=${headerHeight}px`);

                // Use scrollIntoView which respects scroll-margin-top
                el.scrollIntoView({ behavior: "smooth", block: "start" });

                // As extra safety, the manual scrollTo fallback after a short delay (in case scrollIntoView was ignored)
                setTimeout(() => {
                    const rect = el.getBoundingClientRect();
                    const desiredTop = window.pageYOffset + rect.top - (headerHeight + 8);
                    // eslint-disable-next-line no-console
                    console.log(`[ScrollToHashElement] fallback scrollTo to ${Math.round(desiredTop)} (el.top ${Math.round(rect.top)})`);
                    window.scrollTo({ top: desiredTop, behavior: "smooth" });
                }, 350);

                return;
            }

            retries += 1;
            if (retries < maxRetries) {
                setTimeout(tryScroll, delay);
            } else {
                // give one last try with scrollIntoView without offset
                const fallbackEl = document.getElementById(id);
                if (fallbackEl) {
                    // eslint-disable-next-line no-console
                    console.warn(`[ScrollToHashElement] final fallback scrollIntoView for #${id}`);
                    fallbackEl.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }
        };

        // wait a bit for route/component mount and then try (helps avoid early scroll)
        setTimeout(tryScroll, 60);
    }, [hash]);

    return null;
}

export default function UserRoutes() {
    return (
        <>
            {/* 👇 Add the hash-based scroll handler once here */}
            <ScrollToHashElement />

            <Routes>
                {/* Home */}
                <Route path="/" element={<HomePage />} />

                {/* Static pages */}
                <Route path="/terms-and-conditions" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                {/* Optional dynamic pages
        <Route path="/project/:slug" element={<ProjectDetail />} />
        <Route path="/developer/:slug" element={<DeveloperDetail />} />
        */}

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </>
    );
}
